// ─── Scheda del socio → Bot dei soci → gli account Telegram (A7) ────────────
//
// 🗣️ 28/09/2026, regola A7 del regolamento, mockup
// `mockup/a7-account-gia-collegato-mockup.html` approvato (A · A · A · A):
// una scheda ha un account Telegram solo. Chi cambia telefono passa dalla
// Segreteria, che qui scollega il vecchio account col motivo; il socio si
// ricollega da sé, condividendo il numero dal telefono che usa adesso.
//
// ⚖️ Due account insieme sono i collegamenti di prima (scelta 3A): restano
// finché la Segreteria sceglie quale scollegare, e gli avvisi intanto vanno
// solo al più recente.

import { useState } from 'react';
import { MOTIVI_SCOLLEGA, type AccountTelegram as Account } from '@pmo/shared';
import { usaScollegaTelegram, usaTelegramDelSocio } from '../../features/soci/dati.js';
import { Guasto } from '../../components/pezzi.js';

const FUSO = 'Europe/Rome';
const quando = (iso: string) => {
  const d = new Date(iso);
  const giorno = d.toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: FUSO,
  });
  const ora = d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', timeZone: FUSO });
  return `${giorno} alle ${ora}`;
};
const nomeAccount = (a: Pick<Account, 'name' | 'username'>) =>
  [a.name, a.username ? `@${a.username}` : null].filter(Boolean).join(' ') || 'Account Telegram';

const ALTRO = 'altro';

export function AccountTelegram({ id, nome }: { id: string; nome: string }): React.JSX.Element | null {
  const t = usaTelegramDelSocio(id);
  const scollega = usaScollegaTelegram(id);
  const [apre, setApre] = useState<Account | null>(null);
  const [scelto, setScelto] = useState<string>('');
  const [scritto, setScritto] = useState('');

  if (t.error) return <Guasto errore={t.error} />;
  if (!t.data) return null;
  const { linked, history, privacyAcceptedAt } = t.data;
  const motivo = scelto === ALTRO ? scritto.trim() : scelto;
  const chiudi = () => {
    setApre(null);
    setScelto('');
    setScritto('');
  };

  return (
    <div className="account-telegram">
      <h4>Telegram</h4>
      {/* 🔒 29/09/2026 (scelta 8A): il consenso dato dall'assistente, quando è arrivato il contatto. */}
      <p className="piccolo tenue consenso-privacy">
        {privacyAcceptedAt
          ? `🔒 Privacy policy accettata dall’assistente il ${quando(privacyAcceptedAt)}`
          : '🔒 Privacy policy: ancora da accettare dall’assistente (si accetta entrando).'}
      </p>
      {linked.length === 0 ? (
        <p className="piccolo tenue">
          Nessun account collegato: si collega da solo quando {nome} condivide il numero con l’assistente.
        </p>
      ) : (
        <ul className="elenco-account">
          {linked.map((a) => (
            <li key={a.id}>
              <span>
                ✈️ <strong>{nomeAccount(a)}</strong>
              </span>{' '}
              <span className="piccolo tenue">collegato dal {quando(a.linkedAt)}</span>
              {apre === null && (
                <button type="button" className="piatto pericolo" onClick={() => setApre(a)}>
                  Scollega
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {linked.length > 1 && (
        <p className="piccolo avviso">
          Due account agiscono come {nome}, e gli avvisi arrivano solo al più recente. Scollega quello che non
          usa più.
        </p>
      )}

      {apre && (
        <div className="finestra-caparra">
          <p className="piccolo">
            <strong>
              Scollegare {nomeAccount(apre)} da {nome}?
            </strong>{' '}
            Entro un minuto l’assistente smette di riconoscere questo account. {nome} si ricollega
            condividendo il numero dal telefono che usa adesso.
          </p>
          <fieldset className="motivi-scollega">
            <legend className="piccolo">Perché (resta scritto)</legend>
            {MOTIVI_SCOLLEGA.map((m, i) => (
              <label key={m} className="piccolo">
                <input
                  type="radio"
                  name="motivo-scollega"
                  id={`motivo-scollega-${i}`}
                  checked={scelto === m}
                  onChange={() => setScelto(m)}
                />{' '}
                {m}
              </label>
            ))}
            <label className="piccolo">
              <input
                type="radio"
                name="motivo-scollega"
                id="motivo-scollega-altro"
                checked={scelto === ALTRO}
                onChange={() => setScelto(ALTRO)}
              />{' '}
              Altro
            </label>
            {scelto === ALTRO && (
              <input
                id="motivo-scollega-testo"
                aria-label="Scrivi il motivo"
                value={scritto}
                onChange={(e) => setScritto(e.target.value)}
              />
            )}
          </fieldset>
          <div className="azioni-caparra">
            <button
              type="button"
              className="pericolo"
              disabled={motivo.length < 3 || scollega.isPending}
              onClick={() => scollega.mutate({ linkId: apre.id, reason: motivo }, { onSuccess: chiudi })}
            >
              Scollega
            </button>
            <button type="button" className="bordato" onClick={chiudi}>
              Lascia stare
            </button>
          </div>
          <Guasto errore={scollega.error} />
        </div>
      )}

      {history.length > 0 && (
        <ul className="storico">
          {history.map((a) => (
            <li key={a.id} className="annullata piccolo">
              {nomeAccount(a)}, collegato dal {quando(a.linkedAt)}: scollegato il {quando(a.unlinkedAt)}
              {a.unlinkedBy ? ` da ${a.unlinkedBy}` : ''} · {a.reason}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
